import { useEffect, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import { useOptionalWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { projectsApi } from './api';
import { isProjectDraftOutput, rememberProjectDraft, useCreatedProject } from './draft-store';
import { ProjectMark } from './mark';
import { chatProject, rememberProjectPlace, rememberThreadPlace } from './place';
import type { ProjectPlace } from './types';

/** The chat tools whose results render as project cards in the thread. */
export const PROJECT_TOOL_CARDS = new Set([
  'draft_project',
  'suggest_project_move',
  'move_chat_to_project',
  'add_to_project',
  'remove_from_project',
]);

function isPlace(value: unknown): value is ProjectPlace {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as ProjectPlace).id === 'string' &&
    typeof (value as ProjectPlace).name === 'string'
  );
}

/** A project tool's card, or null for an output it doesn't recognise. */
export function ProjectToolCard({ toolName, result }: { toolName: string; result: unknown }) {
  if (toolName === 'draft_project') return <DraftCard output={result} />;
  if (toolName === 'suggest_project_move') return <SuggestMoveCard output={result} />;
  if (toolName === 'move_chat_to_project') return <MovedCard output={result} />;
  if (toolName === 'add_to_project') return <AddedCard output={result} />;
  if (toolName === 'remove_from_project') return <RemovedCard output={result} />;
  return null;
}

/** Whether the reader owns the open chat: only its owner drafts, creates and moves it. */
function ownsChat(ws: ReturnType<typeof useOptionalWorkspace>) {
  return (ws?.threadMeta?.access ?? 'owner') === 'owner';
}

function DraftCard({ output }: { output: unknown }) {
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const valid = isProjectDraftOutput(output);
  const createdHere = useCreatedProject(ws?.threadId ?? '');
  const placed = ws ? chatProject(ws) : null;
  const done =
    createdHere ??
    (valid && placed && output.draft.chatId && output.draft.chatId === ws?.threadId
      ? placed
      : null);
  useEffect(() => {
    if (ws && valid) rememberProjectDraft(ws.threadId, output);
  }, [ws?.threadId, valid, output]);
  if (!ws || !valid || !ownsChat(ws)) return null;
  const { draft, version } = output;
  return (
    <button
      type="button"
      className="ws-artifact-card"
      onClick={() =>
        done ? ws.openProject(done.id) : ws.openCanvas({ kind: 'project-draft', id: ws.threadId })
      }
    >
      <ProjectMark name={draft.name} tint={draft.tint ?? 'sand'} size="sm" />
      <span>
        <strong>{draft.name}</strong>
        <small>
          {done ? copy.projectCreatedOpen : formatCopy(copy.newProjectDraft, { count: version })}
        </small>
      </span>
      <WorkspaceIcon name="arrow" size={15} />
    </button>
  );
}

/** Offered once, under an answer, for a chat that isn't in a project. Nothing moves without the tap. */
function SuggestMoveCard({ output }: { output: unknown }) {
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const [state, setState] = useState<'idle' | 'busy' | 'moved' | 'failed'>('idle');
  const project = (output as { project?: unknown } | null)?.project;
  if (!ws || !isPlace(project) || !ownsChat(ws)) return null;
  const current = chatProject(ws);
  const moved = state === 'moved' || current?.id === project.id;
  const move = async () => {
    setState('busy');
    try {
      const result = await projectsApi.moveChat(ws.threadId, project.id);
      if (result.project) rememberProjectPlace(result.project);
      rememberThreadPlace(ws.threadId, result.project);
      ws.refreshThreadMeta();
      setState('moved');
    } catch {
      setState('failed');
    }
  };
  return (
    <div
      className="ws-project-suggest"
      role="group"
      aria-label={formatCopy(copy.moveToProject, { name: project.name })}
    >
      <ProjectMark name={project.name} tint={project.tint} size="sm" />
      <span>
        {moved
          ? formatCopy(copy.chatInProject, { name: project.name })
          : formatCopy(copy.looksLikeWork, { name: project.name })}
        <small>
          {moved
            ? copy.accessBriefNext
            : state === 'failed'
              ? copy.moveFailed
              : copy.accessBriefNextTypographic}
        </small>
      </span>
      {moved ? (
        <button type="button" className="ws-button" onClick={() => ws.openProject(project.id)}>
          {copy.openProject}
        </button>
      ) : (
        <button
          type="button"
          className="ws-button"
          disabled={state === 'busy' || Boolean(current)}
          onClick={() => void move()}
        >
          {copy.move}
        </button>
      )}
    </div>
  );
}

function MovedCard({ output }: { output: unknown }) {
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const project = (output as { project?: unknown } | null)?.project;
  if (!ws || !output || (output as { status?: string }).status !== 'ok') return null;
  if (!isPlace(project))
    return (
      <div className="ws-tool-progress" role="status">
        <WorkspaceIcon name="check" size={14} />
        {copy.movedOut}
      </div>
    );
  return (
    <button type="button" className="ws-artifact-card" onClick={() => ws.openProject(project.id)}>
      <ProjectMark name={project.name} tint={project.tint} size="sm" />
      <span>
        <strong>
          {copy.in} {project.name}
        </strong>
        <small>{copy.chatLivesHere}</small>
      </span>
      <WorkspaceIcon name="arrow" size={15} />
    </button>
  );
}

function AddedCard({ output }: { output: unknown }) {
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const value = output as { status?: string; project?: unknown; item?: { title?: string } } | null;
  if (!ws || value?.status !== 'ok' || !isPlace(value.project)) return null;
  const project = value.project;
  return (
    <button type="button" className="ws-artifact-card" onClick={() => ws.openProject(project.id)}>
      <ProjectMark name={project.name} tint={project.tint} size="sm" />
      <span>
        <strong>
          {formatCopy(copy.addedTo, {
            item: value.item?.title ?? copy.itIs,
            project: project.name,
          })}
        </strong>
        <small>{copy.addedAccess}</small>
      </span>
      <WorkspaceIcon name="arrow" size={15} />
    </button>
  );
}

function RemovedCard({ output }: { output: unknown }) {
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const value = output as { status?: string; project?: unknown; item?: { title?: string } } | null;
  if (!ws || value?.status !== 'ok') return null;
  const project = isPlace(value.project) ? value.project : null;
  return (
    <div className="ws-tool-progress" role="status">
      <WorkspaceIcon name="check" size={14} />
      {formatCopy(copy.removedOut, {
        item: value.item?.title ?? copy.itIs,
        project: project?.name ?? copy.theProject,
      })}
    </div>
  );
}
